import React, { useMemo } from "react";
import { Clock } from "lucide-react";

/**
 * WindowSelect — 15-minute transportation-window selector.
 *
 * Used by /onboarding (waitlist), /waitlist (legacy form), and the
 * live /schedule booking flow. Values are stored as "HH:MM-HH:MM"
 * strings (e.g. "14:15-14:30"). `formatWindowLabel` renders a value
 * as friendly text ("2:15 PM – 2:30 PM").
 *
 * Two variants: "light" for cream rider surfaces, "dark" for the
 * navy onboarding scene.
 */
export function makeQuarterHourWindows(startHour = 0, endHour = 24) {
  const out = [];
  for (let h = startHour; h < endHour; h += 1) {
    for (let m = 0; m < 60; m += 15) {
      let endH = h;
      let endM = m + 15;
      if (endM === 60) { endM = 0; endH = h + 1; }
      // Allow the closing window of the day (23:45 → 00:00). We store the
      // end as 00:00 (midnight rolls over to the next day). Skipping any
      // window AFTER that would push into the following day.
      if (h === 23 && endH === 24) endH = 0;
      if (endH > 24 || (endH === 24 && endM > 0)) continue;
      const pad = (n) => String(n).padStart(2, "0");
      const value = `${pad(h)}:${pad(m)}-${pad(endH)}:${pad(endM)}`;
      out.push({ value, label: `${fmtTime(h, m)} – ${fmtTime(endH === 0 && h === 23 ? 24 : endH, endM)}` });
    }
  }
  return out;
}

function fmtTime(hh, mm) {
  // 24 → "12:00 AM" of the following day (used only for the closing
  // window label so "11:45 PM – 12:00 AM" reads correctly).
  if (hh === 24) return `12:${String(mm).padStart(2, "0")} AM`;
  const ampm = hh >= 12 ? "PM" : "AM";
  const h12 = ((hh + 11) % 12) + 1;
  return `${h12}:${String(mm).padStart(2, "0")} ${ampm}`;
}

export function formatWindowLabel(value) {
  if (!value || typeof value !== "string") return "—";
  const m = value.match(/^(\d{2}):(\d{2})-(\d{2}):(\d{2})$/);
  if (!m) return value;
  return `${fmtTime(+m[1], +m[2])} – ${fmtTime(+m[3], +m[4])}`;
}

/** Parse just the start time ("HH:MM") from a window value. */
export function windowStart(value) {
  if (!value || typeof value !== "string") return null;
  const m = value.match(/^(\d{2}):(\d{2})-\d{2}:\d{2}$/);
  return m ? `${m[1]}:${m[2]}` : null;
}

/** Build the window value for a given "HH:MM" start time. */
export function windowForStart(startHHMM) {
  if (!startHHMM || typeof startHHMM !== "string") return "";
  const [hh, mm] = startHHMM.split(":").map((n) => parseInt(n, 10));
  if (Number.isNaN(hh) || Number.isNaN(mm)) return "";
  let endH = hh;
  let endM = mm + 15;
  if (endM >= 60) { endM -= 60; endH += 1; }
  if (endH >= 24) endH = 0; // 23:45 → 00:00 wrap (last window of the day)
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(hh)}:${pad(mm)}-${pad(endH)}:${pad(endM)}`;
}

export default function WindowSelect({
  value,
  onChange,
  placeholder = "Select a 15-minute window…",
  variant = "light",
  testId,
  startHour = 0,
  endHour = 24,
}) {
  const options = useMemo(() => makeQuarterHourWindows(startHour, endHour), [startHour, endHour]);
  const isDark = variant === "dark";
  return (
    <div className="relative">
      <select
        value={value || ""}
        onChange={(e) => onChange?.(e.target.value)}
        className={[
          "w-full h-12 rounded-xl pl-4 pr-10 text-[15px] appearance-none focus:outline-none transition-all",
          isDark
            ? "bg-white/[0.04] border border-white/15 text-white focus:border-[#ed6500] focus:ring-2 focus:ring-[#ed6500]/20"
            : "bg-white border border-[#E5E1D8] text-[#0A1128] focus:border-[#ed6500] focus:ring-2 focus:ring-[#ed6500]/15",
        ].join(" ")}
        data-testid={testId}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <Clock
        size={14}
        className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? "text-white/45" : "text-[#515E7B]/60"}`}
      />
    </div>
  );
}
